import React, { useEffect, useState } from 'react';
import { Platform } from 'react-native';

import { Box, Button, ButtonSpinner, ButtonText, Divider, Heading, Text, VStack } from '@gluestack-ui/themed';
import { useNavigation } from '@react-navigation/native';
import * as Linking from 'expo-linking';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import BottomAction from '../../../../shared/components/bottomAction/bottomAction';
import BottomSheet from '../../../../shared/components/bottomSheet/bottomSheet';
import { errorHandler } from '../../../../shared/components/errorHandler/errorHander';
import MediaUploader from '../../../../shared/components/media/mediaUploader';
import { PressableRow } from '../../../../shared/components/pressable/pressableRow';
import UnderLineButton from '../../../../shared/components/pressable/underLineButton';
import errorToast from '../../../../shared/components/toast/errorToast';
import successToast from '../../../../shared/components/toast/successToast';
import WebScrollView from '../../../../shared/components/web/webScrollView';
import { useMaxWidth } from '../../../../shared/provider/maxWidthProvider';
import { firebaseApp } from '../../../../shared/services/firebase/firebaseFactory';
import { Metrics } from '../../../../shared/themes';
import { carDocumentTypes } from '../../../../shared/util/car-utils';
import { cleanFirestoreListResult } from '../../../../shared/util/firestore-utils';
import { userDocumentStatus } from '../../../../shared/util/userDocument-utils';
import CarDocumentSkeleton from '../carDocumentSkeleton';

const emptyDocument = {
  status: userDocumentStatus.waintingApproval,
  type: carDocumentTypes.autorizationOfUse,
};

function AuthorizationOfUse({ route }) {
  const { carId } = route?.params ?? {};

  const currentUser = useSelector((state) => state.users.currentUser);

  const [document, setDocument] = useState(emptyDocument);
  const [updating, setUpdating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isOpen, setOpen] = useState(false);

  const [bottomActionHeight, setBottomActionHeight] = useState(0);

  const { t } = useTranslation();
  const navigation = useNavigation();
  const maxWidth = useMaxWidth();

  function valid() {
    return document?.imagesStatement?.length > 0 && document?.imagesOwnership?.length > 0;
  }

  function SaveDocuments() {
    if (valid()) {
      setUpdating(true);
      if (!document?.uid) {
        firebaseApp
          .firestore()
          .collection('cars')
          .doc(carId)
          .collection('documents')
          .add(document)
          .then(() => {
            setUpdating(false);
            successToast(t('carDocuments.successfullyAdd'));
            navigation.canGoBack() && navigation.goBack();
          })
          .catch((e) => {
            errorHandler(e, true);
            setUpdating(false);
          });
      } else {
        firebaseApp
          .firestore()
          .collection('cars')
          .doc(carId)
          .collection('documents')
          .doc(document?.uid)
          .update(document)
          .then(() => {
            setUpdating(false);
            successToast(t('carDocuments.successfullyAdd'));
            navigation.canGoBack() && navigation.goBack();
          })
          .catch((e) => {
            errorHandler(e, true);
            setUpdating(false);
          });
      }
    } else {
      errorToast(t('error.completeForm'));
    }
  }

  function updateImages(images, field) {
    const cleanImages = images.filter((image) => image?.url !== undefined);
    if (cleanImages && cleanImages?.length > 0) {
      const imagesToUpdate = {};
      imagesToUpdate[field] = cleanImages;
      setDocument((prevDoc) => {
        return { ...prevDoc, ...imagesToUpdate };
      });
    }
  }

  useEffect(() => {
    if (currentUser?.uid && carId) {
      setLoading(true);
      firebaseApp
        .firestore()
        .collection('cars')
        .doc(carId)
        .collection('documents')
        .where('type', '==', carDocumentTypes.autorizationOfUse)
        .get()
        .then(cleanFirestoreListResult)
        .then((res) => {
          if (res?.length > 0) {
            setDocument(res[0]);
          } else {
            setDocument(emptyDocument);
          }
          setLoading(false);
        })
        .catch((e) => {
          errorHandler(e, true);
          setLoading(false);
        });
    }
  }, [currentUser, carId]);

  function openTemplate(url) {
    if (Platform.OS === 'web') {
      window.open(url, '_blank');
    } else {
      Linking.openURL(url);
    }
  }

  if (loading) {
    return <CarDocumentSkeleton />;
  }

  return (
    <>
      <WebScrollView testID={'scrollView'} hideFooter={true} maxWidth={maxWidth > 700 ? 700 : maxWidth}>
        <VStack my={'$4'} mx={'$screenMargin'} space={'md'} mb={bottomActionHeight}>
          <Text variant={'grey'}>{t('carDocuments.ownership.authorizationOfUseSubTitle')}</Text>
          <VStack space={'sm'}>
            <Heading>{t('carDocuments.ownership.authorizationOfUseStatementTitle')}</Heading>
            <Text variant={'grey'} size={'secondary'}>
              {t('carDocuments.ownership.authorizationOfUseStatementInfo')}
            </Text>
            <UnderLineButton
              onPress={() => setOpen(true)}
              text={t('carDocuments.ownership.authorizationOfUseStatementTemplateButton')}
              hideLeftIcon={true}
            />
            <MediaUploader
              key={document?.uid}
              testID={'statement'}
              initialList={document?.imagesStatement ?? []}
              onUpdate={(images) => updateImages(images, 'imagesStatement')}
              storagePath={'user-documents'}
              multipleSelectionAllow={true}
              document={true}
            />
          </VStack>
          <VStack space={'sm'}>
            <Heading>{t('carDocuments.ownership.authorizationOfUseOwnershipTitle')}</Heading>
            <Text variant={'grey'} size={'secondary'}>
              {t('carDocuments.ownership.authorizationOfUseOwnershipInfo')}
            </Text>
            <MediaUploader
              key={document?.uid}
              testID={'ownership'}
              initialList={document?.imagesOwnership ?? []}
              onUpdate={(images) => updateImages(images, 'imagesOwnership')}
              storagePath={'user-documents'}
              multipleSelectionAllow={true}
              document={true}
            />
          </VStack>
        </VStack>
      </WebScrollView>
      <BottomAction
        onLayout={(event) => {
          setBottomActionHeight(event?.nativeEvent?.layout?.height ?? 0);
        }}>
        <Box px={'$screenMargin'} my={'$4'} w={maxWidth > 700 ? 700 : maxWidth} alignSelf={'center'}>
          <Button testID="button.addDocument" isDisabled={updating || !valid()} onPress={SaveDocuments}>
            {updating && <ButtonSpinner mr="$1" />}
            <ButtonText>{t('global.validate')}</ButtonText>
          </Button>
        </Box>
      </BottomAction>
      <BottomSheet
        isOpen={isOpen}
        setOpen={setOpen}
        snapToTop={true}
        headerText={t('carDocuments.ownership.authorizationOfUseStatementTemplateButton')}>
        <VStack mx={'$screenMargin'}>
          <PressableRow
            onPress={() => openTemplate('https://assets.flyandcar.com/assets/template-authorization-of-use-en.pdf')}
            text={t('carDocuments.ownership.authorizationOfUseStatementTemplateEnglish')}
          />
          <Divider />
          <PressableRow
            onPress={() => openTemplate('https://assets.flyandcar.com/assets/template-authorization-of-use-es.pdf')}
            text={t('carDocuments.ownership.authorizationOfUseStatementTemplateSpanish')}
          />
          <Divider />
          <PressableRow
            onPress={() => openTemplate('https://assets.flyandcar.com/assets/template-authorization-of-use-fr.pdf')}
            text={t('carDocuments.ownership.authorizationOfUseStatementTemplateFrench')}
          />
        </VStack>
      </BottomSheet>
    </>
  );
}

export default AuthorizationOfUse;
